import { FC, useCallback, useState } from 'react'
import {
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  Paper,
  Radio,
  RadioGroup,
  Slider,
  Stack,
  Typography,
} from '@mui/material'
import CloseIcon from '@mui/icons-material/Close'
import SaveIcon from '@mui/icons-material/Save'
import { startPlaybook } from '../../../services/playbook.service'
import { useToast } from '../../../contexts/toast.context'

interface LabeledSliderProps {
  label: string
  min?: number
  max?: number
  step?: number
  unit?: string
  defaultValue?: number
  onChange?: (value: number) => void
}

export const LabeledSlider: React.FC<LabeledSliderProps> = ({
  label,
  min = 0,
  max = 100,
  step = 1,
  unit = '',
  defaultValue = min,
  onChange,
}) => {
  const [value, setValue] = useState<number>(defaultValue)

  const handleChange = (_event: Event, newValue: number | number[]) => {
    const n = newValue as number
    setValue(n)
    onChange?.(n)
  }

  return (
    <Box display="flex" alignItems="center" width="100%" gap={2}>
      <Typography sx={{ width: '130px', flexGrow: 0, flexShrink: 0, whiteSpace: 'nowrap', color: 'text.secondary' }}>
        {label}:
      </Typography>

      <Slider
        value={value}
        min={min}
        max={max}
        step={step}
        onChange={handleChange}
        sx={{ flexGrow: 1 }}
        aria-label={label}
      />

      <Typography variant="body1" sx={{ width: '80px', flexGrow: 0, flexShrink: 0, whiteSpace: 'nowrap' }}>
        {value} {unit && <span>{unit}</span>}
      </Typography>
    </Box>
  )
}

interface IProps {
  onCancel: () => void
}

const ObfuscationForm: FC<IProps> = ({ onCancel }) => {
  const { showToast } = useToast()

  const [timeBuffer, setTimeBuffer] = useState(15)
  const [frequencyBuffer, setFrequencyBuffer] = useState(10)
  const [locationBuffer, setLocationBuffer] = useState(25)
  const [enableDecoys, setEnableDecoys] = useState(true)
  const [decoyRate, setDecoyRate] = useState(3)
  const [realismLevel, setRealismLevel] = useState(1)
  const [generalizeIncumbentType, setGeneralizeIncumbentType] = useState(true)
  const [includeConfidenceScore, setIncludeConfidenceScore] = useState(true)
  const [includeAuthoritySource, setIncludeAuthoritySource] = useState(true)

  const handleSave = useCallback(async () => {
    try {
      await startPlaybook('obfuscation', {
        time_buffer_min: timeBuffer,
        frequency_buffer_mhz: frequencyBuffer,
        location_buffer_km: locationBuffer,
        enable_decoys: enableDecoys,
        decoy_rate_per_hour: decoyRate,
        realism_level: realismLevel,
        generalize_incumbent_type: generalizeIncumbentType,
        include_confidence_score: includeConfidenceScore,
        include_authority_source: includeAuthoritySource,
      })
      showToast('success', 'Obfuscation settings saved')
      onCancel()
    } catch {
      // axios interceptor already surfaced the error to the user
    }
  }, [
    timeBuffer,
    frequencyBuffer,
    locationBuffer,
    enableDecoys,
    decoyRate,
    realismLevel,
    generalizeIncumbentType,
    includeConfidenceScore,
    includeAuthoritySource,
    onCancel,
    showToast,
  ])

  return (
    <Box p={2}>
      <Stack spacing={2}>
        <Typography variant="h6">Obfuscation Settings</Typography>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Parameter Padding</Typography>
            <LabeledSlider
              label="Time Buffer"
              min={0}
              max={20}
              defaultValue={timeBuffer}
              step={1}
              unit="min"
              onChange={setTimeBuffer}
            />
            <LabeledSlider
              label="Frequency Buffer"
              min={0}
              max={30}
              defaultValue={frequencyBuffer}
              step={1}
              unit="MHz"
              onChange={setFrequencyBuffer}
            />
            <LabeledSlider
              label="Location Buffer"
              min={0}
              max={30}
              defaultValue={locationBuffer}
              step={1}
              unit="km"
              onChange={setLocationBuffer}
            />
          </Stack>
        </Paper>

        <Paper sx={{ p: 2 }}>
          <Stack spacing={1}>
            <Typography fontWeight="bold">Decoy Notifications</Typography>
            <Box display="flex" alignItems="center" width="100%" gap={2}>
              <Typography
                sx={{ width: '120px', flexGrow: 0, flexShrink: 0, whiteSpace: 'nowrap', color: 'text.secondary' }}
              >
                Enable Decoys:
              </Typography>
              <Checkbox checked={enableDecoys} onChange={(e) => setEnableDecoys(e.target.checked)} />
            </Box>

            <LabeledSlider
              label="Decoy Rate"
              min={0}
              max={10}
              defaultValue={decoyRate}
              step={1}
              unit="/ hour"
              onChange={setDecoyRate}
            />

            <Box display="flex" alignItems="center" width="100%" gap={2} sx={{ '& label': { mb: 0 } }}>
              <Typography
                sx={{ width: '130px', flexGrow: 0, flexShrink: 0, whiteSpace: 'nowrap', color: 'text.secondary' }}
              >
                Realism Level:
              </Typography>
              <FormControl>
                <RadioGroup row value={realismLevel} onChange={(e) => setRealismLevel(Number(e.target.value))}>
                  <FormControlLabel control={<Radio />} value={1} label="Low" />
                  <FormControlLabel control={<Radio />} value={2} label="Medium" />
                  <FormControlLabel control={<Radio />} value={3} label="High" />
                </RadioGroup>
              </FormControl>
            </Box>
          </Stack>
        </Paper>

        <Paper sx={{ p: 2, '& label': { mb: 0 } }}>
          <Stack>
            <Typography fontWeight="bold" mb={1}>
              Information Masking
            </Typography>

            <Stack direction="row">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={generalizeIncumbentType}
                    onChange={(e) => setGeneralizeIncumbentType(e.target.checked)}
                  />
                }
                label="Generalize incumbent type"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    sx={{ ml: 2 }}
                    checked={includeConfidenceScore}
                    onChange={(e) => setIncludeConfidenceScore(e.target.checked)}
                  />
                }
                label="Include confidence score"
              />
            </Stack>
            <Stack direction="row">
              <FormControlLabel
                control={
                  <Checkbox
                    checked={includeAuthoritySource}
                    onChange={(e) => setIncludeAuthoritySource(e.target.checked)}
                  />
                }
                label="Include authority source"
              />
            </Stack>
          </Stack>
        </Paper>

        <Stack direction="row" justifyContent="end" spacing={1}>
          <Button variant="text" startIcon={<CloseIcon />} size="small" onClick={onCancel}>
            Cancel
          </Button>

          <Button variant="contained" onClick={handleSave} startIcon={<SaveIcon />} size="small">
            Save
          </Button>
        </Stack>
      </Stack>
    </Box>
  )
}

export default ObfuscationForm
